/**
 * Propriétés pour les taches de type message
 * Cela va être enregistré dans le BPMN sous la forme :
 * <bpmn:sendTask id="a" name="mail" 
 *  aktor:type="email" 
 *  aktor:recipient="other"
 *  aktor:email="exemple1@aktor.fr,exemple2@aktor.fr">
 * 
 * aktor:type :
 * - 'email' (ou vide) pour un e-mail
 * - 'sms' pour un SMS
 * 
 * aktor:recipient :
 * - 'contact' (ou vide) pour envoyer le message au contact
 * - 'user' pour envoyer le message à l'utilisateur qui a créé le sendout
 * - 'other' pour une adresse e-mail personnalisée
 * 
 * aktor:email :
 * - l'adresse e-mail à laquelle envoyer le message.
 * - seulement si le type est 'email'
 * - éventuellement plusieurs adresses e-mail séparées par des virgules ','
 */
import { html } from 'htm/preact';
import { SelectEntry, isSelectEntryEdited, TextFieldEntry, isTextFieldEntryEdited } from '@bpmn-io/properties-panel';
import { useService } from 'bpmn-js-properties-panel';
import EventBus from '@services/EventBus';

// Écouter l'événement de mise à jour du JSON
EventBus.on('updateMessageJson', (data) => {
  const modeling = data.modeling;
  modeling.updateProperties(data.element, {
    'message-json': data.json
  });
});

function MessageProps(element) {
  let fields = [{
    id: 'type',
    element,
    component: TypeField,
    isEdited: isSelectEntryEdited
  }, {
    id: 'recipient',
    element,
    component: RecipientField,
    isEdited: isSelectEntryEdited
  }, {
    id: 'email',
    element,
    component: EmailField,
    isEdited: isTextFieldEntryEdited
  }, {
    id: 'action-button',
    element,
    component: ActionButton
  }];

  return fields;
}

function TypeField({ element, id }) {

  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getValue = () => {
    return element.businessObject['type'] || 'email';
  };

  const setValue = (value) => {
    let result = {};
    result['type'] = value;
    return modeling.updateProperties(element, result);
  };

  const getOptions = () => {
    return [
      { value: 'email', label: translate('E-mail') },
      { value: 'sms', label: translate('SMS') }
    ];
  };

  return html`<${SelectEntry}
    id=${id}
    element=${element}
    label=${translate('Type')}
    getValue=${getValue}
    setValue=${setValue}
    getOptions=${getOptions}
    debounce=${debounce}
  />`;
}

function RecipientField({ element, id }) {

  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getValue = () => {
    return element.businessObject['recipient'] || 'contact';
  };

  const setValue = (value) => {
    let result = {};
    result['recipient'] = value;
    return modeling.updateProperties(element, result);
  };

  const getOptions = () => {
    if (element.businessObject['type'] === 'email' || !element.businessObject['type']) {
      return [
        { value: 'contact', label: translate('Contact') },
        { value: 'user', label: translate('User who created the sendout') },
        { value: 'other', label: translate('Other email address') },
      ];
    } else {
      return [
        { value: 'contact', label: translate('Contact') },
      ];
    }
  };

  return html`<${SelectEntry}
    id=${id}
    element=${element}
    label=${translate('Recipient')}
    getValue=${getValue}
    setValue=${setValue}
    getOptions=${getOptions}
    debounce=${debounce}
  />`;
}

function EmailField({ element, id }) {

  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getValue = () => {
    return element.businessObject['email'] || '';
  };

  const setValue = (value) => {
    let result = {};
    result['email'] = value;
    return modeling.updateProperties(element, result);
  };

  const isVisible = () => {
    return (element.businessObject['type'] === 'email' || !element.businessObject['type']) 
      && element.businessObject['recipient'] === 'other';
  };

  return isVisible() ? html`<${TextFieldEntry}
    id=${id}
    element=${element}
    label=${translate('Email')}
    getValue=${getValue}
    setValue=${setValue}
    debounce=${debounce}
    tooltip=${translate("You can enter multiple email addresses separated by commas ','.")}
  />` : '';
}

function ActionButton({ element }) {
  const translate = useService('translate');
  const modeling = useService('modeling');

  const handleClick = () => {
    const type = element.businessObject['type'] || 'email';
    EventBus.emit('openMessageDialog', { element, modeling, type });
  };

  return html`
    <div class="bio-properties-panel-entry" style="margin-top: 17px;">
      <button 
        type="button"
        class="bpp-button" 
        onClick=${handleClick}
      >
        ${translate('Configure message')}
      </button>
    </div>
  `;
}

export default MessageProps;